import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Toaster } from "sonner";
import LoginPage from "./features/auth/presentation/pages/LoginPage";
import ForgotPasswordPage from "./features/auth/presentation/pages/ForgotPasswordPage";
import SignupPage from "./features/auth/presentation/pages/SignupPage";
import Layout from "./features/auth/presentation/components/Layout";
import SetupPasswordPage from "./features/auth/presentation/pages/SetupPasswordPage";
import SuccessfulRegistrationPage from "./features/auth/presentation/pages/SuccessfulRegistrationPage";
import SetupTwoFAPage from "./features/auth/presentation/pages/SetupTwoFAPage";
import ScanQRPage from "./features/auth/presentation/pages/ScanQRPage";
import TotpPage from "./features/auth/presentation/pages/TotpPage";
import SuccessfulTwoFASetup from "./features/auth/presentation/pages/SuccessfulTwoFASetup";
import VerifyOtpPage from "./features/auth/presentation/pages/VerifyOtpPage";
import SuccessfulForgotPasswordPage from "./features/auth/presentation/pages/SuccessfulForgotPasswordPage";
import { RolesPermissionsPage } from "./features/roles-permissions/presentation";
import { ProfilePage } from "./features/profile/presentation";
import MembersGroupsPage from "./features/members-groups/presentation/pages/MembersGroups";
import Patients from "./features/patients/presentation/pages/Patients";
import Appointments from "./features/appointments/presentation/pages/Appointments";
import PatientDetails from "./features/patients/presentation/pages/PatientDetails";
import NeedsAction from "./features/needs-action/presentation/pages/NeedsAction";
import FaqPage from "./features/auth/presentation/pages/FaqPage";
import SettingsPage from "./features/auth/presentation/pages/SettingsPage";
import AboutPage from "./features/auth/presentation/pages/AboutPage";
import { readSharedAuthItem } from "@/core/api/sharedAuthStorage";
import { useLanguageSync } from "@/features/user-settings/application/hooks/useLanguageSync";

const isAuthenticated = () => Boolean(readSharedAuthItem("access_token"));

// Redirects unauthenticated users to /login, replacing the history entry so
// the back button cannot return to the protected page.
const PrivateRoute = () =>
  isAuthenticated() ? <Outlet /> : <Navigate to="/login" replace />;

// Redirects an already-authenticated user away from the login page.
const PublicOnlyRoute = () =>
  isAuthenticated() ? <Navigate to="/patients" replace /> : <Outlet />;

const AppRoutes = () => {
  // Runs for every route (public and private) so a signed-in user's saved
  // language is checked against the API as soon as a session exists —
  // including right after logging out and back in — not only once the
  // private area has mounted.
  useLanguageSync();

  return (
    <BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Login — redirect to dashboard if already logged in */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Auth flow routes (accessible regardless of session state) */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        <Route
          path="/register-success"
          element={<SuccessfulRegistrationPage />}
        />
        <Route path="/setup-twofa" element={<SetupTwoFAPage />} />
        <Route path="/scan-qr" element={<ScanQRPage />} />
        <Route path="/twofa-success" element={<SuccessfulTwoFASetup />} />
        <Route path="/verification-code" element={<TotpPage />} />
        <Route path="/verify-email-otp" element={<VerifyOtpPage />} />
        <Route
          path="/forgot-password-success"
          element={<SuccessfulForgotPasswordPage />}
        />

        {/* Private Routes — require a valid session */}
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route
              path="/roles-permissions"
              element={<RolesPermissionsPage />}
            />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/members-groups" element={<MembersGroupsPage />} />
            <Route path="/patients" element={<Patients />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/needs-action" element={<NeedsAction />} />
            <Route path="/patients/:patientId" element={<PatientDetails />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
